import { create } from '@bufbuild/protobuf';

import { unauthorizedWsCallsRequestManager } from '../UnauthorizedWsCallsApi';
import type { CallPermissions } from '../types/call/call_model_pb';
import {
  ClearPermissionsForMemberRequest,
  ClearPermissionsForMemberRequestSchema,
  CreatePermissionRequestRequest,
  CreatePermissionRequestRequestSchema,
  FinishForAllRequest,
  FinishForAllRequestSchema,
  ForceMuteRequest,
  ForceMuteRequestSchema,
  GetCallMembersByIdsRequest,
  GetCallMembersByIdsRequestSchema,
  GetCallMembersByCameraOrScreenIdsRequest,
  GetCallMembersByCameraOrScreenIdsRequestSchema,
  GetCallMembersRequest,
  GetCallMembersRequestSchema,
  GetCallPermissionsRequest,
  GetCallPermissionsRequestSchema,
  GetPermissionRequestsRequest,
  GetPermissionRequestsRequestSchema,
  KickMemberRequest,
  KickMemberRequestSchema,
  SetCallPermissionsForAllRequest,
  SetCallPermissionsForAllRequestSchema,
  SetCallPermissionsForMemberRequest,
  SetCallPermissionsForMemberRequestSchema,
  UpdatePermissionRequestForAllRequest,
  UpdatePermissionRequestForAllRequestSchema,
  UpdatePermissionRequestForMemberRequest,
  UpdatePermissionRequestForMemberRequestSchema,
  GetCallByIdRequest,
  GetCallByIdRequestSchema,
  JoinToCallRequest,
  JoinToCallRequestSchema,
  GetCallLinkRequest,
  GetCallLinkRequestSchema,
  WaitingRoomAccessRequest,
  WaitingRoomAccessRequestSchema,
} from '../types/call/call_requests_pb';
import {
  ClearPermissionsForMemberResponse,
  CreatePermissionRequestResponse,
  FinishForAllResponse,
  ForceMuteResponse,
  GetCallByIdResponse,
  GetCallLinkResponse,
  GetCallMembersByIdsResponse,
  GetCallMembersByCameraOrScreenIdsResponse,
  GetCallMembersResponse,
  GetCallPermissionsResponse,
  GetPermissionRequestsResponse,
  JoinToCallResponse,
  KickMemberResponse,
  SetCallPermissionsForAllResponse,
  SetCallPermissionsForMemberResponse,
  UpdatePermissionRequestForAllResponse,
  UpdatePermissionRequestForMemberResponse,
  WaitingRoomAccessResponse,
} from '../types/call/call_responses_pb';
import { WsRequest } from './request.types';

export function getCallByIdRequest(
  data: WsRequest<GetCallByIdRequest>
): Promise<GetCallByIdResponse> {
  if (!unauthorizedWsCallsRequestManager) {
    throw new Error('unauthorizedWsCallsRequestManager should be initialized');
  }
  return unauthorizedWsCallsRequestManager.request<GetCallByIdResponse>({
    case: 'getCallInfo',
    value: create(GetCallByIdRequestSchema, data),
  });
}

export function joinToCallRequest(data: WsRequest<JoinToCallRequest>): Promise<JoinToCallResponse> {
  if (!unauthorizedWsCallsRequestManager) {
    throw new Error('unauthorizedWsCallsRequestManager should be initialized');
  }
  return unauthorizedWsCallsRequestManager.request<JoinToCallResponse>({
    case: 'joinToCall',
    value: create(JoinToCallRequestSchema, data),
  });
}

export function finishCallForAllRequest(
  data: WsRequest<FinishForAllRequest>
): Promise<FinishForAllResponse> {
  if (!unauthorizedWsCallsRequestManager) {
    throw new Error('unauthorizedWsCallsRequestManager should be initialized');
  }
  return unauthorizedWsCallsRequestManager.request<FinishForAllResponse>({
    case: 'finishForAll',
    value: create(FinishForAllRequestSchema, data),
  });
}

export function kickMemberRequest(data: WsRequest<KickMemberRequest>): Promise<KickMemberResponse> {
  if (!unauthorizedWsCallsRequestManager) {
    throw new Error('unauthorizedWsCallsRequestManager should be initialized');
  }
  return unauthorizedWsCallsRequestManager.request<KickMemberResponse>({
    case: 'kickMember',
    value: create(KickMemberRequestSchema, data),
  });
}

export function forceMuteRequest(data: WsRequest<ForceMuteRequest>): Promise<ForceMuteResponse> {
  if (!unauthorizedWsCallsRequestManager) {
    throw new Error('unauthorizedWsCallsRequestManager should be initialized');
  }
  return unauthorizedWsCallsRequestManager.request<ForceMuteResponse>({
    case: 'forceMute',
    value: create(ForceMuteRequestSchema, data),
  });
}

export function getCallMembersRequest(
  data: WsRequest<GetCallMembersRequest>
): Promise<GetCallMembersResponse> {
  if (!unauthorizedWsCallsRequestManager) {
    throw new Error('unauthorizedWsCallsRequestManager should be initialized');
  }
  return unauthorizedWsCallsRequestManager.request<GetCallMembersResponse>({
    case: 'getCallMembers',
    value: create(GetCallMembersRequestSchema, data),
  });
}

export function getCallMembersByUidRequest(
  data: WsRequest<GetCallMembersByCameraOrScreenIdsRequest>
): Promise<GetCallMembersByCameraOrScreenIdsResponse> {
  if (!unauthorizedWsCallsRequestManager) {
    throw new Error('unauthorizedWsCallsRequestManager should be initialized');
  }
  return unauthorizedWsCallsRequestManager.request<GetCallMembersByCameraOrScreenIdsResponse>({
    case: 'getCallMembersByCameraOrScreenIds',
    value: create(GetCallMembersByCameraOrScreenIdsRequestSchema, data),
  });
}

export function getCallMembersByIdsRequest(
  data: WsRequest<GetCallMembersByIdsRequest>
): Promise<GetCallMembersByIdsResponse> {
  if (!unauthorizedWsCallsRequestManager) {
    throw new Error('unauthorizedWsCallsRequestManager should be initialized');
  }
  return unauthorizedWsCallsRequestManager.request<GetCallMembersByIdsResponse>({
    case: 'getCallMembersByIds',
    value: create(GetCallMembersByIdsRequestSchema, data),
  });
}

export function getCallPermissionsRequest(
  data: WsRequest<GetCallPermissionsRequest>
): Promise<GetCallPermissionsResponse> {
  if (!unauthorizedWsCallsRequestManager) {
    throw new Error('unauthorizedWsCallsRequestManager should be initialized');
  }
  return unauthorizedWsCallsRequestManager.request<GetCallPermissionsResponse>({
    case: 'getCallPermissions',
    value: create(GetCallPermissionsRequestSchema, data),
  });
}

export function getCallPermissionRequestsRequest(
  data: WsRequest<GetPermissionRequestsRequest>
): Promise<GetPermissionRequestsResponse> {
  if (!unauthorizedWsCallsRequestManager) {
    throw new Error('unauthorizedWsCallsRequestManager should be initialized');
  }
  return unauthorizedWsCallsRequestManager.request<GetPermissionRequestsResponse>({
    case: 'getPermissionRequests',
    value: create(GetPermissionRequestsRequestSchema, data),
  });
}

export function clearCallPermissionsForMemberRequest(
  data: WsRequest<ClearPermissionsForMemberRequest>
): Promise<ClearPermissionsForMemberResponse> {
  if (!unauthorizedWsCallsRequestManager) {
    throw new Error('unauthorizedWsCallsRequestManager should be initialized');
  }
  return unauthorizedWsCallsRequestManager.request<ClearPermissionsForMemberResponse>({
    case: 'clearPermissionsForMember',
    value: create(ClearPermissionsForMemberRequestSchema, data),
  });
}

export function createCallPermissionRequestRequest(
  data: WsRequest<CreatePermissionRequestRequest>
): Promise<CreatePermissionRequestResponse> {
  if (!unauthorizedWsCallsRequestManager) {
    throw new Error('unauthorizedWsCallsRequestManager should be initialized');
  }
  return unauthorizedWsCallsRequestManager.request<CreatePermissionRequestResponse>({
    case: 'createPermissionRequest',
    value: create(CreatePermissionRequestRequestSchema, data),
  });
}

export function setCallPermissionsForAllRequest(
  data: WsRequest<
    Omit<SetCallPermissionsForAllRequest, 'default'> & { default: WsRequest<CallPermissions> }
  >
): Promise<SetCallPermissionsForAllResponse> {
  if (!unauthorizedWsCallsRequestManager) {
    throw new Error('unauthorizedWsCallsRequestManager should be initialized');
  }
  return unauthorizedWsCallsRequestManager.request<SetCallPermissionsForAllResponse>({
    case: 'setCallPermissionsForAll',
    value: create(SetCallPermissionsForAllRequestSchema, data),
  });
}

export function setCallPermissionsForMemberRequest(
  data: WsRequest<SetCallPermissionsForMemberRequest>
): Promise<SetCallPermissionsForMemberResponse> {
  if (!unauthorizedWsCallsRequestManager) {
    throw new Error('unauthorizedWsCallsRequestManager should be initialized');
  }
  return unauthorizedWsCallsRequestManager.request<SetCallPermissionsForMemberResponse>({
    case: 'setCallPermissionsForMember',
    value: create(SetCallPermissionsForMemberRequestSchema, data),
  });
}

export function updateCallPermissionRequestForAllRequest(
  data: WsRequest<UpdatePermissionRequestForAllRequest>
): Promise<UpdatePermissionRequestForAllResponse> {
  if (!unauthorizedWsCallsRequestManager) {
    throw new Error('unauthorizedWsCallsRequestManager should be initialized');
  }
  return unauthorizedWsCallsRequestManager.request<UpdatePermissionRequestForAllResponse>({
    case: 'updatePermissionRequestForAll',
    value: create(UpdatePermissionRequestForAllRequestSchema, data),
  });
}

export function updateCallPermissionRequestForMemberRequest(
  data: WsRequest<UpdatePermissionRequestForMemberRequest>
): Promise<UpdatePermissionRequestForMemberResponse> {
  if (!unauthorizedWsCallsRequestManager) {
    throw new Error('unauthorizedWsCallsRequestManager should be initialized');
  }
  return unauthorizedWsCallsRequestManager.request<UpdatePermissionRequestForMemberResponse>({
    case: 'updatePermissionRequestForMember',
    value: create(UpdatePermissionRequestForMemberRequestSchema, data),
  });
}

export function getCallLinkRequest(
  data: WsRequest<GetCallLinkRequest>
): Promise<GetCallLinkResponse> {
  if (!unauthorizedWsCallsRequestManager) {
    throw new Error('unauthorizedWsCallsRequestManager should be initialized');
  }

  return unauthorizedWsCallsRequestManager.request<GetCallLinkResponse>({
    case: 'getCallLink',
    value: create(GetCallLinkRequestSchema, data),
  });
}

export function waitingRoomAccessRequest(
  data: WsRequest<WaitingRoomAccessRequest>
): Promise<WaitingRoomAccessResponse> {
  if (!unauthorizedWsCallsRequestManager) {
    throw new Error('unauthorizedWsCallsRequestManager should be initialized');
  }
  return unauthorizedWsCallsRequestManager.request<WaitingRoomAccessResponse>({
    case: 'waitingRoomAccess',
    value: create(WaitingRoomAccessRequestSchema, data),
  });
}
